import { useState } from "react";
import api, { fmtErr } from "../api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Question, Wrench, PaperPlaneTilt, Sparkle, EnvelopeSimple } from "@phosphor-icons/react";

export default function Help() {
  const [tab, setTab] = useState(null); // null | "question" | "problem"
  const [q, setQ] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");

  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/support/ask", { question: q, history });
      setHistory([...history, { q, a: r.data.answer }]);
      setQ("");
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  const report = async () => {
    if (!subject.trim() || !description.trim()) return toast.error("Add a subject and describe the problem");
    setBusy(true);
    try {
      await api.post("/support/report", { subject, description });
      toast.success("Sent — we'll reply by email shortly");
      setSubject(""); setDescription(""); setTab(null);
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <Layout title="Help & support">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3" data-testid="help-tabs">
          <button data-testid="help-question-tab" onClick={() => setTab("question")}
            className={`card-flat flex flex-col items-center gap-2 p-6 transition-colors ${tab === "question" ? "border-2 border-primary" : ""}`}>
            <Question size={28} weight="bold" className="text-primary" />
            <span className="text-sm font-bold">I have a question</span>
            <span className="text-xs text-muted-foreground">Instant answers from the assistant</span>
          </button>
          <button data-testid="help-problem-tab" onClick={() => setTab("problem")}
            className={`card-flat flex flex-col items-center gap-2 p-6 transition-colors ${tab === "problem" ? "border-2 border-primary" : ""}`}>
            <Wrench size={28} weight="bold" className="text-primary" />
            <span className="text-sm font-bold">Report a problem</span>
            <span className="text-xs text-muted-foreground">Goes straight to Veloktify support</span>
          </button>
        </div>

        {tab === "question" && (
          <section className="card-flat space-y-3 p-5" data-testid="help-ai">
            {history.map((h, i) => (
              <div key={`${i}-${h.q.length}`} className="space-y-2">
                <p className="rounded-md bg-muted p-3 text-sm font-semibold">{h.q}</p>
                <p className="rounded-md border border-border p-3 text-sm">{h.a}</p>
              </div>
            ))}
            <div className="flex gap-2">
              <input data-testid="help-question-input" className="field-input flex-1" placeholder="e.g. How do I quote an add-on?" value={q}
                onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} />
              <button data-testid="help-ask-btn" onClick={ask} disabled={busy}
                className="flex h-12 shrink-0 items-center gap-2 rounded-md bg-primary px-4 text-sm font-bold text-primary-foreground disabled:opacity-40">
                <Sparkle size={16} weight="bold" /> {busy ? "…" : "Ask"}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Assistant answers how-to questions. Not helping? <button data-testid="help-escalate" onClick={() => setTab("problem")} className="font-bold text-primary underline">Report a problem instead</button></p>
          </section>
        )}

        {tab === "problem" && (
          <section className="card-flat space-y-3 p-5" data-testid="help-form">
            <input data-testid="help-subject" className="field-input w-full" placeholder="Subject (e.g. Can't connect Stripe)" value={subject} onChange={(e) => setSubject(e.target.value)} />
            <textarea data-testid="help-description" className="field-input h-32 w-full py-3" placeholder="What happened? What were you trying to do? Include any error message you saw." value={description} onChange={(e) => setDescription(e.target.value)} />
            <button data-testid="help-send-btn" onClick={report} disabled={busy}
              className="btn-primary-big !h-14 !text-base disabled:opacity-40">
              <PaperPlaneTilt size={18} weight="bold" className="mr-2 inline" /> {busy ? "Sending…" : "Send to support"}
            </button>
          </section>
        )}

        <div className="card-flat flex items-center justify-between p-4 text-sm" data-testid="help-email-row">
          <span className="text-muted-foreground">Prefer email?</span>
          <a href="mailto:support@veloktify.com" data-testid="help-email-link" className="flex items-center gap-2 font-bold text-primary">
            <EnvelopeSimple size={16} weight="bold" /> support@veloktify.com
          </a>
        </div>
      </div>
    </Layout>
  );
}
